import {
    Row,
    RowList,
    Tag,
    Text6,
    Spinner,
    IconChevronLeftRegular,
    Text2,
    Text3,
    Stack,
    ThemeVariant,
    NavigationBar,
    NavigationBarAction,
    NavigationBarActionGroup,
    IconInformationUserRegular,
    skinVars,
    useDialog,
    IconPinRegular,
} from '@telefonica/mistica';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SkeletonMobile from '../../../components/SkeletonMobile';
import { ISwitch } from '../../../models/ConfigureLine';
import { getCallForwardingInfo } from '../../../services/ProductService';
import { getRoleLine } from '../../../utils/dataTransformation/productConsumption';
import { formatMobileNumber } from '../../../utils/dataTransformation/unitsConversion';
import './index.scss';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import { useConfigureLineContext } from '../../../utils/store/context/configure-line-context/ConfigureLineContext';
import { useQuery } from '@tanstack/react-query';
import { getSIMSInfo } from '../../../services/ConfigureLineService';
import BreadCrumbsWrapper from '../../../components/BreadcrumbsWrapper';

const CallDivert: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { trackingScreenView } = new TrackingEvents();
    const consumptions = JSON.stringify(window.localStorage.getItem('storages'));
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<ISwitch[]>([]);
    const [windowWidth, setWindowWidth] = useState<any>(window.innerWidth);
    const { dialog } = useDialog();
    const { lineActive, isConvergent } = useConfigureLineContext();

    const { data: simInfo } = useQuery(['sims-info'], () => (lineActive ? getSIMSInfo(lineActive) : null), {
        enabled: !!lineActive,
    });

    const handleInfo = () => {
        dialog({
            title: t('callDivert'),
            icon: (() => {
                if (windowWidth >= 1024) {
                    return <IconPinRegular color={skinVars.colors.brand} size={64} />;
                }
            })() as any,
            message: (() => {
                return (
                    <Stack space={16}>
                        <Text3 light color={skinVars.colors.textSecondary}>
                            {t('calldivertalerttext1')}
                        </Text3>
                        <Text3 light color={skinVars.colors.textSecondary}>
                            {t('calldivertalerttext2')}
                        </Text3>
                        <Text3 light color={skinVars.colors.textSecondary}>
                            {t('calldivertalerttext3')}
                        </Text3>
                    </Stack>
                );
            })() as any,
            onAccept() {},
            acceptText: t('calldivertalerttextbtn'),
        });
    };

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
        getCallForwardingInfo(simInfo?.number!)
            .then((results: any) => {
                setData([
                    {
                        restrictionSpecId: '1',
                        title: t('BlockedNumberTitle1'),
                        subtitle:
                            (results.data.always ? t('BlockedNumberDesact') : t('BlockedNumberAct')) +
                            t('BlockedNumberdescription1'),
                        state: results.data.always,
                    },
                    {
                        restrictionSpecId: '2',
                        title: t('BlockedNumberTitle2'),
                        subtitle:
                            (results.data.onCommunicate ? t('BlockedNumberDesact') : t('BlockedNumberAct')) +
                            t('BlockedNumberdescription2'),
                        state: results.data.onCommunicate,
                    },
                    {
                        restrictionSpecId: '3',
                        title: t('BlockedNumberTitle3'),
                        subtitle:
                            (results.data.onNoResponse ? t('BlockedNumberDesact') : t('BlockedNumberAct')) +
                            t('BlockedNumberdescription3'),
                        state: results.data.onNoResponse,
                    },
                    {
                        restrictionSpecId: '4',
                        title: t('BlockedNumberTitle4'),
                        subtitle:
                            (results.data.onOffline ? t('BlockedNumberDesact') : t('BlockedNumberAct')) +
                            t('BlockedNumberdescription4'),
                        state: results.data.onOffline,
                    },
                ]);
                setLoading(false);
            })
            .catch((err: AxiosError) => {
                console.error(err);

                setLoading(false);
            });

        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const body = () => {
        return (
            <div>
                <RowList role='rowList'>
                    {data.map((card: ISwitch, index) => {
                        const { title, subtitle, state } = card;
                        return (
                            <div key={index}>
                                <Row
                                    title={title}
                                    subtitle={subtitle}
                                    headline={
                                        <Tag type={state ? 'success' : 'inactive'}>
                                            {t(`${state ? 'activated' : 'disabled'}`)}
                                        </Tag>
                                    }
                                />
                            </div>
                        );
                    })}
                </RowList>
            </div>
        );
    };

    return (
        <SkeletonMobile
            header={
                <div className='call-divert__header'>
                    {!(isWebviewAndroid || isWebviewIOS) && (
                        <>
                            <div className='call-divert__header-navigationBar'>
                                <NavigationBar
                                    isInverse={false}
                                    onBack={() => {
                                        navigate(`/configure-line`);
                                    }}
                                    title={t('callDivert')}
                                    right={
                                        <NavigationBarActionGroup>
                                            <NavigationBarAction
                                                aria-label='exit'
                                                onPress={() => {
                                                    handleInfo();
                                                }}
                                            >
                                                <IconInformationUserRegular size={24} />
                                            </NavigationBarAction>
                                        </NavigationBarActionGroup>
                                    }
                                />
                            </div>
                            <div
                                className='call-divert__header-return'
                                onClick={() => {
                                    navigate(`/configure-line`);
                                }}
                            >
                                <div className='call-divert__header-return-icon'>
                                    <IconChevronLeftRegular color={skinVars.colors.textPrimary} size={20} />
                                </div>
                                <Text2 medium color={skinVars.colors.textPrimary}>
                                    {t('returnResponsive')}
                                </Text2>

                                <div onClick={handleInfo} className='call-divert__header-info'>
                                    <IconInformationUserRegular size={24} color='white' />
                                </div>
                            </div>
                        </>
                    )}
                    <div className='call-divert__breadcrumbs'>
                        <ThemeVariant>
                            <BreadCrumbsWrapper
                                title={t('callDivert')}
                                breadcrumbs={
                                    !isConvergent
                                        ? [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: t('lineManagement'),
                                                  url: `/configure-line`,
                                              },
                                          ]
                                        : [
                                              { title: t('breadcrumbsHome'), url: '/home' },
                                              {
                                                  title: getRoleLine(JSON.parse(consumptions), simInfo?.productPsId!),
                                                  url: `/mobile?productPsId=${simInfo?.productPsId}`,
                                              },
                                              {
                                                  title: t('lineManagement'),
                                                  url: `/configure-line`,
                                              },
                                          ]
                                }
                            />
                        </ThemeVariant>
                    </div>
                    <div className='roaming-page__header-number'>
                        <Text6 color={skinVars.colors.textPrimary}>{formatMobileNumber(simInfo?.number)}</Text6>
                    </div>
                </div>
            }
            body={loading ? <Spinner size={24} /> : body()}
        ></SkeletonMobile>
    );
};

export default CallDivert;
